import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { maintenanceApi, MaintStation, MaintStationDevice, OrgItem, StationTypeItem, GstStateItem, StationSettings } from "../../../api/maintenance";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { canEditMaintenance } from "../shared/MaintenanceGuard";
import { ArrowLeft, Save, Trash2, Upload, X, Zap } from "lucide-react";
import { useNotice } from "../shared/useNotice";

const StationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [station, setStation] = useState<MaintStation | null>(null);
  const [orgs, setOrgs] = useState<OrgItem[]>([]);
  const [stationTypes, setStationTypes] = useState<StationTypeItem[]>([]);
  const [gstStates, setGstStates] = useState<GstStateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [devices, setDevices] = useState<MaintStationDevice[]>([]);
  const [editingPosition, setEditingPosition] = useState<number | null>(null);
  const [positionValue, setPositionValue] = useState("");
  const [message, setMessage] = useNotice("success", "");
  const [error, setError] = useNotice("error", "");

  // Editable fields
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [orgId, setOrgId] = useState<number>(0);
  const [locationLat, setLocationLat] = useState("");
  const [locationLon, setLocationLon] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [stationTypeId, setStationTypeId] = useState<number | "">("");
  const [gstStateCode, setGstStateCode] = useState<number | "">("");

  // Power-loss auto-resume station settings
  const [settings, setSettings] = useState<StationSettings | null>(null);
  const [autoRestartEnabled, setAutoRestartEnabled] = useState(false);
  const [staleTimeoutMinutes, setStaleTimeoutMinutes] = useState<number | "">(5);
  const [holdMinutes, setHoldMinutes] = useState<number | "">(5);
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchStation = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [stationRes, orgsRes, typesRes, gstRes, devicesRes, settingsRes] = await Promise.all([
        maintenanceApi.getStation(id),
        maintenanceApi.getOrgs(),
        maintenanceApi.getStationTypes(),
        maintenanceApi.getGstStates(),
        maintenanceApi.getStationDevices(id),
        maintenanceApi.getStationSettings(id),
      ]);
      const s = stationRes.data;
      setStation(s);
      setOrgs(orgsRes.data);
      setStationTypes(typesRes.data);
      setGstStates(gstRes.data);
      setDevices(devicesRes.data);
      setSettings(settingsRes.data);
      setAutoRestartEnabled(settingsRes.data.auto_restart_enabled);
      setStaleTimeoutMinutes(settingsRes.data.stale_timeout_minutes);
      setHoldMinutes(settingsRes.data.auto_restart_hold_minutes);
      setName(s.name || "");
      setAddress(s.text_address || "");
      setOrgId(s.org_id);
      setLocationLat(s.location_lat?.toString() || "");
      setLocationLon(s.location_lon?.toString() || "");
      setIsActive(s.is_active ?? true);
      setStationTypeId(s.charging_station_type_id || "");
      setGstStateCode(s.gst_state_code || "");
    } catch (err) {
      setError("Failed to load station");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchStation();
  }, [fetchStation]);

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data: Record<string, any> = {};
      if (name !== station?.name) data.name = name;
      if (address !== (station?.text_address || "")) data.text_address = address;
      if (orgId !== station?.org_id) data.org_id = orgId;
      if (locationLat && parseFloat(locationLat) !== station?.location_lat) data.location_lat = parseFloat(locationLat);
      if (locationLon && parseFloat(locationLon) !== station?.location_lon) data.location_lon = parseFloat(locationLon);
      if (isActive !== station?.is_active) data.is_active = isActive;
      const typeVal = stationTypeId === "" ? null : stationTypeId;
      if (typeVal !== station?.charging_station_type_id) data.charging_station_type_id = typeVal;
      const gstVal = gstStateCode === "" ? null : gstStateCode;
      if (gstVal !== station?.gst_state_code) data.gst_state_code = gstVal;

      if (Object.keys(data).length === 0) {
        setMessage("No changes to save");
        setSaving(false);
        return;
      }

      await maintenanceApi.updateStation(id, data);
      setMessage("Station updated successfully");
      fetchStation();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!id) return;
    setSavingSettings(true);
    setError("");
    setMessage("");
    try {
      const data: Record<string, any> = {};
      if (autoRestartEnabled !== settings?.auto_restart_enabled) data.auto_restart_enabled = autoRestartEnabled;
      if (staleTimeoutMinutes !== "" && staleTimeoutMinutes !== settings?.stale_timeout_minutes)
        data.stale_timeout_minutes = staleTimeoutMinutes;
      if (holdMinutes !== "" && holdMinutes !== settings?.auto_restart_hold_minutes)
        data.auto_restart_hold_minutes = holdMinutes;

      if (Object.keys(data).length === 0) {
        setMessage("No settings changes to save");
        setSavingSettings(false);
        return;
      }

      // Guard: hold window must fit inside the stale window, or a resumed session
      // could be force-finished before it has a chance to come back.
      const effHold = staleTimeoutMinutes === "" ? Number(holdMinutes) : Number(holdMinutes);
      const effStale = staleTimeoutMinutes === "" ? Number(settings?.stale_timeout_minutes) : Number(staleTimeoutMinutes);
      if (data.auto_restart_enabled !== false && Number(effHold) > Number(effStale)) {
        setError("Hold minutes must be ≤ stale-timeout minutes");
        setSavingSettings(false);
        return;
      }

      const res = await maintenanceApi.updateStationSettings(id, data);
      setSettings(res.data);
      setAutoRestartEnabled(res.data.auto_restart_enabled);
      setStaleTimeoutMinutes(res.data.stale_timeout_minutes);
      setHoldMinutes(res.data.auto_restart_hold_minutes);
      setMessage("Auto-resume settings saved");
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setUploadingLogo(true);
    setError("");
    try {
      await maintenanceApi.uploadStationLogo(id, file);
      setMessage("Logo uploaded");
      fetchStation();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleLogoDelete = async () => {
    if (!id || !station?.station_logo_url) return;
    if (!window.confirm("Remove station logo?")) return;
    setError("");
    try {
      await maintenanceApi.deleteStationLogo(id);
      setMessage("Logo removed");
      fetchStation();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to remove logo");
    }
  };

  const handleDelete = async () => {
    if (!id || !station) return;
    if (!window.confirm(`Delete station "${station.name}"? This cannot be undone.`)) return;
    try {
      await maintenanceApi.deleteStation(id);
      navigate("/maintenance/stations");
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const handlePositionSave = async (deviceId: number) => {
    if (!id) return;
    const pos = parseInt(positionValue);
    if (isNaN(pos)) {
      setError("Position must be a number");
      return;
    }
    try {
      await maintenanceApi.updateDevicePosition(id, deviceId, pos);
      setEditingPosition(null);
      setMessage("Device position updated");
      fetchStation();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to update position");
    }
  };

  if (loading) return <MaintenanceLayout title="Station Detail"><p>Loading...</p></MaintenanceLayout>;
  if (!station) return <MaintenanceLayout title="Station Detail"><p>Station not found</p></MaintenanceLayout>;

  return (
    <MaintenanceLayout title="Station Detail">
      <div className="maint-detail">
        <button className="maint-btn-link" onClick={() => navigate("/maintenance/stations")}>
          <ArrowLeft size={16} /> Back to Stations
        </button>

        <div className="maint-detail-card">
          <div className="maint-detail-header">
            <h3>{station.name}</h3>
            <span className="maint-detail-guid">{station.charging_station_guid}</span>
          </div>

          {/* Logo section */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ fontWeight: 600, display: "block", marginBottom: "8px" }}>Station Logo</label>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {station.station_logo_url ? (
                <>
                  <img src={station.station_logo_url} alt="Station logo" style={{ width: 80, height: 80, borderRadius: 8, objectFit: "cover", border: "1px solid #e0e0e0" }} />
                  {canEditMaintenance() && (
                    <button className="maint-btn-small" onClick={handleLogoDelete} title="Remove logo">
                      <X size={14} /> Remove
                    </button>
                  )}
                </>
              ) : (
                <span style={{ color: "#999" }}>No logo</span>
              )}
              {canEditMaintenance() && (
                <>
                  <input type="file" ref={fileInputRef} accept="image/*" onChange={handleLogoUpload} style={{ display: "none" }} />
                  <button className="maint-btn-small" onClick={() => fileInputRef.current?.click()} disabled={uploadingLogo}>
                    <Upload size={14} /> {uploadingLogo ? "Uploading..." : "Upload"}
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="maint-form-grid">
            <label>
              Name
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label>
              Organization
              <select value={orgId} onChange={(e) => setOrgId(Number(e.target.value))}>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </label>
            <label style={{ gridColumn: "1 / -1" }}>
              Address
              <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} />
            </label>
            <label>
              Latitude
              <input type="number" step="any" value={locationLat} onChange={(e) => setLocationLat(e.target.value)} />
            </label>
            <label>
              Longitude
              <input type="number" step="any" value={locationLon} onChange={(e) => setLocationLon(e.target.value)} />
            </label>
            <label>
              GST State
              <select value={gstStateCode} onChange={(e) => setGstStateCode(e.target.value ? Number(e.target.value) : "")}>
                <option value="">Select GST state...</option>
                {gstStates.map((g) => (
                  <option key={g.state_code} value={g.state_code}>{g.state_name} ({g.state_short})</option>
                ))}
              </select>
            </label>
            <label>
              Station Type
              <select value={stationTypeId} onChange={(e) => setStationTypeId(e.target.value ? Number(e.target.value) : "")}>
                <option value="">None</option>
                {stationTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </label>
            <label>
              Active
              <select value={isActive ? "true" : "false"} onChange={(e) => setIsActive(e.target.value === "true")}>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </label>
          </div>

          {/* Read-only info */}
          <div className="maint-detail-info" style={{ marginTop: "12px" }}>
            <p><strong>Onboard Date:</strong> {station.onboard_date ? new Date(station.onboard_date).toLocaleDateString() : "-"}</p>
            {station.device_count !== undefined && (
              <p><strong>Devices:</strong> {station.device_count} total, {station.active_devices} active</p>
            )}
          </div>

          {/* Linked Devices */}
          <div style={{ marginTop: "24px" }}>
            <h4 style={{ fontSize: "15px", fontWeight: 600, marginBottom: "12px", display: "flex", alignItems: "center", gap: "6px" }}>
              <Zap size={16} /> Linked Devices ({devices.length})
            </h4>
            {devices.length > 0 ? (
              <table className="maint-table">
                <thead>
                  <tr>
                    <th>Position</th>
                    <th>Name</th>
                    <th>OCPP ID</th>
                    <th>Status</th>
                    <th>Last Active</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((d) => (
                    <tr key={d.id}>
                      <td>
                        {!canEditMaintenance() ? (
                          <span>{d.station_position ?? "-"}</span>
                        ) : editingPosition === d.id ? (
                          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                            <input
                              type="number"
                              value={positionValue}
                              onChange={(e) => setPositionValue(e.target.value)}
                              style={{ width: "60px", padding: "4px 8px", border: "1px solid #e0e0e0", borderRadius: "4px", fontSize: "13px" }}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handlePositionSave(d.id);
                                if (e.key === "Escape") setEditingPosition(null);
                              }}
                            />
                            <button className="maint-btn-small" onClick={() => handlePositionSave(d.id)}>
                              <Save size={12} />
                            </button>
                            <button className="maint-btn-icon" onClick={() => setEditingPosition(null)}>
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <span
                            style={{ cursor: "pointer", borderBottom: "1px dashed #999" }}
                            onClick={() => { setEditingPosition(d.id); setPositionValue(String(d.station_position ?? 1)); }}
                            title="Click to edit position"
                          >
                            {d.station_position ?? "-"}
                          </span>
                        )}
                      </td>
                      <td>{d.display_name || d.unique_name || "-"}</td>
                      <td className="maint-mono">{d.ocpp_device_id || "-"}</td>
                      <td>
                        <span className={`maint-status maint-status-${d.device_status}`}>
                          {d.device_status}
                        </span>
                      </td>
                      <td style={{ fontSize: "13px", color: "#666" }}>
                        {d.last_active ? new Date(d.last_active).toLocaleString() : "-"}
                      </td>
                      <td>
                        <button
                          className="maint-btn-link"
                          style={{ marginBottom: 0, fontSize: "13px" }}
                          onClick={() => navigate(`/maintenance/devices/${d.id}`)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ color: "#999", fontSize: "14px" }}>No devices linked to this station</p>
            )}
          </div>

          {message && <p className="maint-success">{message}</p>}
          {error && <p className="maint-form-error">{error}</p>}

          {canEditMaintenance() && (
            <div className="maint-detail-actions">
              <button onClick={handleSave} disabled={saving} className="maint-btn-primary">
                <Save size={16} /> {saving ? "Saving..." : "Save Changes"}
              </button>
              <button onClick={handleDelete} className="maint-btn-danger">
                <Trash2 size={16} /> Delete Station
              </button>
            </div>
          )}
        </div>

        {canEditMaintenance() && settings && (
          <div className="maint-detail-card" style={{ marginTop: 24 }}>
            <div className="maint-detail-header">
              <h3 style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Zap size={16} /> Power-loss auto-resume
              </h3>
            </div>
            <p style={{ fontSize: 13, color: "#666", marginTop: 4 }}>
              For power-unstable stations. When enabled, a session cut off by a power loss is
              automatically recreated once power returns and the connector reports the EV is
              still plugged in. The hold window is how long users are asked to keep their
              vehicle connected; it must be ≤ the stale-session timeout (otherwise the session
              is force-finished before it can resume).
            </p>
            <div className="maint-form-grid">
              <label style={{ gridColumn: "1 / -1", flexDirection: "row", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={autoRestartEnabled}
                  onChange={(e) => setAutoRestartEnabled(e.target.checked)}
                />
                Enable auto-restart for this station
              </label>
              <label>
                Hold window (minutes)
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={holdMinutes}
                  onChange={(e) => setHoldMinutes(e.target.value ? Number(e.target.value) : "")}
                />
              </label>
              <label>
                Stale-session timeout (minutes)
                <input
                  type="number"
                  min={1}
                  max={120}
                  value={staleTimeoutMinutes}
                  onChange={(e) => setStaleTimeoutMinutes(e.target.value ? Number(e.target.value) : "")}
                />
              </label>
            </div>
            <div className="maint-detail-actions">
              <button onClick={handleSaveSettings} disabled={savingSettings} className="maint-btn-primary">
                <Save size={16} /> {savingSettings ? "Saving..." : "Save Auto-resume Settings"}
              </button>
            </div>
          </div>
        )}
      </div>
    </MaintenanceLayout>
  );
};

export default StationDetail;
